import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import queryString from 'query-string';
import moment from 'moment';

import { useTheme } from '@material-ui/core/styles';

import {
  makeRowColumn,
  getHeadObj,
  cenversionTpCode,
  dayColor,
  converParameters,
  replaceZero,
  converDisplayValue,
  dataConverter,
  getHost,
} from 'lib/commonLib';
import { openrtbStatistics, openrtbGraph } from 'lib/api/openrtb';

import MbTooltip from 'components/atoms/MbTooltip';
import NewButton from 'components/molecules/NewButton';
import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import IconAreaWrap from 'components/molecules/MbTable/IconAreaWrap';
import BarGraph from 'components/organisms/Chart/BarGraph';
import NewCommonFilter from 'components/organisms/NewCommonFilter';
import SortLabelWrap from 'components/molecules/MbTable/SortLabelWrap';

import RtbItlTpCodeFilter from './RtbItlTpCodeFilter';

const Statistics = (props) => {
  const { t } = useTranslation();
  const { location, handlePortalUrl, commonCustomized } = props;
  const { tp_code, isPop } = queryString.parse(location.search);
  const { common } = useSelector(({ common }) => ({ common: common }));
  const theme = useTheme();

  let initParams = {
    order: 'STATS_DTTM',
    sort: 'desc',
    sDate: moment().format('YYYYMM01'),
    eDate: moment().format('YYYYMMDD'),
    itlTpCode: !!tp_code ? tp_code : '04',
    pltfomTpCode: 'all',
  };
  const initCustomized = {
    STATS_DTTM: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'STATS_DTTM');
        let result;
        if (rowObj['FIXED'] === 0) {
          result = t('합계');
        } else {
          if (!!rowObj['PLTFOM_TP_CODE']) {
            result = cenversionTpCode(rowObj['PLTFOM_TP_CODE']);
          } else {
            result = dayColor(
              rowObj['STATS_DTTM'],
              bodyItem1.type,
              bodyItem1.format
            );
          }
        }
        return result;
      },
    },
    ADVER_CNT: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'OT_ADVER_CNT');
        const bodyItem2 = getHeadObj(headsMap, 'ADVER_CNT');
        const tooltipText = (
          <>
            1. 클릭, 소진이 '1'이상 발생한 광고주 수
            <br />
            2. 노출, 클릭, 컨버전 중 데이터가 '1'이상 발생한 광고주 수
          </>
        );
        return (
          rowObj['FIXED'] !== 0 &&
          !rowObj['isChild'] && (
            <MbTooltip title={tooltipText} placement="right-start">
              {makeRowColumn(
                rowObj['OT_ADVER_CNT'],
                bodyItem1.type,
                bodyItem1.format
              )}
              <br />
              {makeRowColumn(
                rowObj['ADVER_CNT'],
                bodyItem2.type,
                bodyItem2.format
              )}
            </MbTooltip>
          )
        );
      },
    },
    OT_ADVER_CNT: { merged: true },
    FIXED: { merged: true },
  };

  let convertKeys = [
    'sDate',
    'eDate',
    'itlTpCode',
    'advrtsPrdtCode',
    'advrtsTpCode',
    'pltfomTpCode',
    'mediaScriptNo',
  ];
  const newInitParams = dataConverter(
    queryString.parse(location.search),
    convertKeys
  );
  initParams = { ...initParams, ...newInitParams };
  const [initialization, setInitialization] = useState(false);
  const [params, setParams] = useState(initParams);
  const [tempParams, setTempParams] = useState(initParams);
  const [customized, setCustomized] = useState(initCustomized);

  const graphList = [
    {
      title: t('지출금액'),
      data: { bar: ['ADVRTS_AMT'] },
      yAxisData: { left: { ADVRTS_AMT: true }, right: {} },
      referenceLine: {
        ADVRTS_AMT: {
          type: 'average',
          label: t('평균선'),
          visibleValue: true,
          color: theme.palette.common.blue,
        },
      },
    },
    {
      title: t('낙찰수'),
      data: { bar: ['WIN_BID_CNT'] },
      yAxisData: { left: { WIN_BID_CNT: true }, right: {} },
      referenceLine: {
        WIN_BID_CNT: {
          type: 'average',
          label: t('평균선'),
          visibleValue: true,
        },
      },
    },
    {
      title: 'CTR',
      data: { bar: ['CLICK_RATE'] },
      yAxisData: { left: { CLICK_RATE: true, isPercent: true }, right: {} },
      referenceLine: {
        CLICK_RATE: { type: 'average', label: t('평균선'), visibleValue: true },
      },
      customizeData: {
        CLICK_RATE: { tooltipFormat: '0.000', isPercent: true },
      },
    },
    {
      title: 'ECPM',
      data: { bar: ['ECPM'] },
      yAxisData: { left: { ECPM: true }, right: {} },
      referenceLine: {
        ECPM: { type: 'average', label: t('평균선'), visibleValue: true },
      },
    },
    {
      title: t('세션ROAS'),
      data: { bar: ['SESSION_ROAS'] },
      yAxisData: { left: { SESSION_ROAS: true, isPercent: true }, right: {} },
      referenceLine: {
        SESSION_ROAS: {
          type: 'average',
          label: t('평균선'),
          visibleValue: true,
        },
      },
      customizeData: { SESSION_ROAS: { isPercent: true } },
    },
    {
      title: t('입찰요청수'),
      data: { bar: ['BID_REQ_CNT'] },
      yAxisData: { left: { BID_REQ_CNT: true }, right: {} },
      referenceLine: {
        BID_REQ_CNT: {
          type: 'average',
          label: t('평균선'),
          visibleValue: true,
        },
      },
    },
    {
      title: t('입찰수'),
      data: { bar: ['BID_CNT'] },
      yAxisData: { left: { BID_CNT: true }, right: {} },
      referenceLine: {
        BID_CNT: { type: 'average', label: t('평균선'), visibleValue: true },
      },
    },
    {
      title: t('입찰률'),
      data: { bar: ['BID_RATE'] },
      yAxisData: { left: { BID_RATE: true, isPercent: true }, right: {} },
      referenceLine: {
        BID_RATE: { type: 'average', label: t('평균선'), visibleValue: true },
      },
      customizeData: { BID_RATE: { isPercent: true } },
    },
    {
      title: t('낙찰률'),
      data: { bar: ['WIN_BID_RATE'] },
      yAxisData: { left: { WIN_BID_RATE: true, isPercent: true }, right: {} },
      referenceLine: {
        WIN_BID_RATE: {
          type: 'average',
          label: t('평균선'),
          visibleValue: true,
        },
      },
      customizeData: { WIN_BID_RATE: { isPercent: true } },
    },
    {
      title: t('입찰/낙찰평균ECPM'),
      data: { bar: ['BID_ECPM'], line: ['WIN_BID_ECPM'] },
      yAxisData: { left: { BID_ECPM: true }, right: {} },
      referenceLine: {
        BID_ECPM: { type: 'average', label: t('평균선'), visibleValue: true },
      },
      customizeData: {
        BID_ECPM: { tooltipFormat: '0.00' },
        WIN_BID_ECPM: { tooltipFormat: '0.00' },
      },
    },
    {
      title: t('수익률'),
      data: { bar: ['MEDIA_PYMNT_AMT_RATE'] },
      yAxisData: {
        left: { MEDIA_PYMNT_AMT_RATE: true, isPercent: true },
        right: {},
      },
      referenceLine: {
        MEDIA_PYMNT_AMT_RATE: {
          type: 'average',
          label: t('평균선'),
          visibleValue: true,
        },
      },
      customizeData: {
        MEDIA_PYMNT_AMT_RATE: { tooltipFormat: '0.00', isPercent: true },
      },
    },
  ];

  useEffect(() => {
    handleCustom(initParams);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    } // eslint-disable-next-line
  }, [initialization]);

  const handleCustom = (propsParams = params) => {
    let newCustomized = { ...initCustomized };
    newCustomized = {
      ...newCustomized,
      CLICK_CNT: {
        makeBody: (rowObj, headsMap) => {
          const bodyItem1 = getHeadObj(headsMap, 'CLICK_CNT');
          const isTotal = rowObj['FIXED'] !== 0;
          const data = makeRowColumn(
            rowObj['CLICK_CNT'],
            bodyItem1.type,
            bodyItem1.format
          );
          let showWindowUrl;
          let result;
          if (isTotal) {
            showWindowUrl = converParameters({
              url: getHost('new') + '/report/adver/statistics',
              sDate: rowObj['STATS_DTTM'],
              eDate: rowObj['STATS_DTTM'],
              itlTpCode: propsParams['itlTpCode'],
              pltfomTpCode: propsParams['pltfomTpCode'],
              advrtsPrdtCode: propsParams['advrtsPrdtCode'],
              advrtsTpCode: propsParams['advrtsTpCode'],
              mediaScriptNo: rowObj['MEDIA_SCRIPT_NO'],
              table: 'kakao_popup',
              popGroup: 'adver',
              isPop: 1,
              uri: '/report/adver/statistics',
              baseAddress: '/v2/openrtb/manage_openrtb_day_report.php',
            });
            result = (
              <IconAreaWrap
                text={data}
                icon={isTotal && !rowObj.isChild && 'faExternalLink'}
                toolTipTitle={t('광고주별 통계')}
                onClick={(e) => handlePortalUrl(showWindowUrl)}
              />
            );
          } else {
            result = data;
          }
          return result;
        },
      },
      ORDER_CNT: {
        makeBody: (rowObj, headsMap) => {
          const bodyItem1 = getHeadObj(headsMap, 'ORDER_CNT');
          const isTotal = rowObj['FIXED'] !== 0 && !isPop;
          const data = makeRowColumn(
            rowObj['ORDER_CNT'],
            bodyItem1.type,
            bodyItem1.format
          );
          let showWindowUrl;
          if (isTotal) {
            showWindowUrl = converParameters({
              url:
                getHost() +
                '/v2/popup/daily_conversion.php',
              gb: converDisplayValue(
                common.productCodeList,
                propsParams['advrtsPrdtCode'],
                propsParams['advrtsTpCode']
              ),
              gb_web: replaceZero(propsParams['pltfomTpCode']),
              st_date: moment(rowObj['STATS_DTTM'] + '').format('YYYY-MM-DD'),
              en_date: moment(rowObj['STATS_DTTM'] + '').format('YYYY-MM-DD'),
              mcgb: 'R',
              tp_code: propsParams['itlTpCode'],
            });
          }
          return isTotal && !rowObj.isChild ? (
            <IconAreaWrap
              text={data}
              icon={'faExternalLink'}
              toolTipTitle={t('해당날짜의 컨버전 내역')}
              onClick={(e) => handlePortalUrl(showWindowUrl)}
            />
          ) : (
            data
          );
        },
      },
      BID_REQ_CNT: {
        makeHead: (headsMap) => {
          return {
            content: (
                <>
                  <SortLabelWrap
                      params={propsParams}
                      label={getHeadObj(headsMap, 'BID_REQ_CNT').label}
                      orderKey="BID_REQ_CNT"
                      onClick={(e) => handleSortChange(propsParams, 'BID_REQ_CNT')}
                  />
                  <br />
                  {getHeadObj(headsMap, 'BID_CNT').label}
                  <br />
                  {getHeadObj(headsMap, 'BID_RATE').label}
                </>
            ),
          };
        },
        makeBody: (rowObj, headsMap) => {
          const bodyItem1 = getHeadObj(headsMap, 'BID_REQ_CNT');
          const bodyItem2 = getHeadObj(headsMap, 'BID_CNT');
          const bodyItem3 = getHeadObj(headsMap, 'BID_RATE');
          return (
              <>
                {makeRowColumn(
                    rowObj['BID_REQ_CNT'],
                    bodyItem1.type,
                    bodyItem1.format
                )}
                <br />
                {makeRowColumn(rowObj['BID_CNT'], bodyItem2.type, bodyItem2.format)}
                <br />
                {makeRowColumn(
                    rowObj['BID_RATE'],
                    bodyItem3.type,
                    bodyItem3.format
                )}
              </>
          );
        },
      },
      BID_CNT: { merged: true },
      BID_RATE: { merged: true },
    };
    setCustomized(newCustomized);
  };

  const handleSortChange = (propsParams, orderKey) => {
    const { order, sort } = propsParams;
    const isDesc = order === orderKey && sort === 'desc';
    const converSort = isDesc ? 'asc' : 'desc';
    const newParams = { ...propsParams, order: orderKey, sort: converSort };
    setParams(newParams);
    handleCustom(newParams);
  };

  const handleOnReset = () => {
    if (
      JSON.stringify(params) !== JSON.stringify(initParams) ||
      JSON.stringify(tempParams) !== JSON.stringify(initParams)
    ) {
      setInitialization(true);
      setParams(initParams);
      setTempParams(initParams);
      handleCustom(initParams);
    }
  };

  const getParam = () => {
    if (JSON.stringify(params) !== JSON.stringify(tempParams)) {
      setParams(tempParams);
      handleCustom(tempParams);
    }
  };

  const getTableParam = (newParams, name) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
      setTempParams(newParams);
      handleCustom(newParams);
    }
  };

  const getTempParam = (newParams) => {
    if (JSON.stringify(tempParams) !== JSON.stringify(newParams)) {
      setTempParams(newParams);
    }
  };

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          params={tempParams}
          getParam={getTempParam}
          useExternalFilter={false}
          pltfomTpCodeDetailFilter={['04']}
        />
        <RtbItlTpCodeFilter params={tempParams} getTempParam={getTempParam} />
        <NewButton type="filterButton" onClick={getParam} disableRipple>
          {t('검색')}
        </NewButton>
      </NewFilterWrap>
      {!isPop && (
        <BarGraph
          params={params}
          dataReqPromise={openrtbGraph}
          graphList={graphList}
          title={t('일자별 RTB 데이터 그래프')}
          labelType="MMDD"
        />
      )}
      <NewTableContainer
        params={params}
        dataReqPromise={openrtbStatistics}
        getParam={getTableParam}
        customized={{ ...customized, ...commonCustomized }}
        expandTable
        useSearch={false}
        useTotalSubList
        isIncludingTotal
      />
    </>
  );
};

export default withRouter(Statistics);
